import React, { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import App, { AppContext, AppInitialProps, AppProps } from 'next/app';
import getConfig from 'next/config';
import Head from 'next/head';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Hydrate } from 'react-query/hydration';
import { createGlobalStyle } from 'styled-components';
import { setAxiosSetup } from 'common/utils/helpers/api.helpers';
import { I18N_DEFAULT_LANGUAGE, I18N_DEFAULT_NAMESPACE, i18nNextInit } from 'common/utils/helpers/i18next';
import i18next from 'i18next';
import { AppThemeProvider } from 'common/stores/theme.store';
import { Layout } from 'common/components/layout/layout';
import { getSSRCookie } from 'common/utils/helpers/cookies.helpers';
import { getSession, SessionProvider } from 'next-auth/react';
import { AuthGuard } from 'modules/auth/components/auth-provider';
import { QUERY_CLIENT_CONFIG } from 'common/utils/constants/api.constants';
import { GlobalStylesOverrides } from 'common/components/layout/layout.styles';
// @ts-ignore
import { Helmet } from 'react-helmet';
import { useRouter } from 'next/router';

const { publicRuntimeConfig } = getConfig();

interface CustomAppProps {
  Component?: any;
  preferredLanguage: string;
  initialLanguageBundle: Record<string, string>;
  session: never;
}

const getDefaultLayout = (page: ReactElement): ReactNode => <Layout>{page}</Layout>;

const pageViewUtagTrigger = (pathname: string) => {
  if (typeof window !== 'undefined' && window.utag && window.utag.view) {
    if (window.utagLastPage === pathname) {
      return;
    }

    window.utagLastPage = pathname;
    //split  path into chunks to determine page
    //and asssign tealium page name
    let page = 'none';
    let tmpSubPath = '';
    const tmp = pathname.split('?');

    if (tmp[0]) {
      const tmpArr = tmp[0].split('/');
      const mainPages = {
        dashboard: 'Dashboard',
        policies: 'Policies',
        quotes: 'Quotes',
        claims: 'Claims',
        billing: 'Billing',
        profile: 'Profile',
      };
      let tmpPath = '';

      switch (tmpArr.length) {
        case 2:
          //for main pages
          tmpPath = tmpArr[1];
          // @ts-ignore
          page = mainPages[tmpPath];
          break;

        case 3:
          //for details pages
          tmpPath = tmpArr[1];
          if (tmpPath === 'policies') {
            page = 'Policy details';
          } else if (tmpPath === 'quotes') {
            page = 'Quote details';
          } else if (tmpPath === 'claims') {
            page = 'Claim details';
          }

          break;

        case 4:
          //subpages
          tmpPath = tmpArr[1];
          tmpSubPath = tmpArr[3];

          if (tmpPath === 'policies') {
            if (tmpSubPath === 'documents') {
              page = 'Policy documents';
            } else if (tmpSubPath === 'invoices') {
              page = 'Policy invoices';
            } else {
              page = 'Policy line details';
            }
          } else if (tmpPath === 'quotes') {
            if (tmpSubPath === 'documents') {
              page = 'Quote Documents';
            } else if (tmpSubPath === 'invoices') {
              page = 'Policy invoices';
            } else {
              page = 'Quote line details';
            }
          }

          break;

        default:
          page = 'none';
      }
    }

    if (page !== 'none') {
      window.utag.view({
        application_name: 'sapiens-digital',
        business_area: 'forsikring',
        measure_source: 'view',
        page_path: pathname,
        page_name: page,
      });
    }
  }
};

function CustomApp({
  Component,
  pageProps,
  preferredLanguage,
  initialLanguageBundle,
  session,
}: AppProps & CustomAppProps): JSX.Element {
  const queryClient = useMemo(() => new QueryClient(QUERY_CLIENT_CONFIG), []);

  const [interval, setInterval] = useState(publicRuntimeConfig.refetchInterval);

  const router = useRouter();

  useEffect(() => {
    const routeHandler = (pathname: string) => {
      pageViewUtagTrigger(pathname);
    };

    router.events.on('routeChangeComplete', routeHandler);

    return () => {
      router.events.off('routeChangeComplete', routeHandler);
    };
  }, [router]);

  pageViewUtagTrigger(router.pathname);

  useMemo(() => {
    setAxiosSetup();
    i18nNextInit({ useLanguageDetector: true, useBackend: true, preferredLanguage });
    i18next.addResourceBundle(preferredLanguage, I18N_DEFAULT_NAMESPACE, initialLanguageBundle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLayout = Component.getLayout || getDefaultLayout;
  //tealium to enable debugging
  /*{
    type: 'text/javascript',
      innerHTML: 'document.cookie="utagdb=true";',
  },*/

  return (
    <AppThemeProvider>
      <GlobalStyle />
      <GlobalStylesOverrides />
      <Helmet
        script={[
          {
            type: 'text/javascript',
            innerHTML: 'var utag_data = {};',
          },
          {
            type: 'text/javascript',
            innerHTML: 'window.utag_cfg_ovrd = {noview : true};',
          },

          {
            type: 'text/javascript',
            innerHTML:
              '(function(a,b,c,d){a="//tags.tiqcdn.com/utag/landkreditt/main/' +
              publicRuntimeConfig.tealiumEnvironment +
              '/utag.js";b=document;c="script";d=b.createElement(c);d.src=a;d.type="text/java"+c;d.async=true;a=b.getElementsByTagName(c)[0];a.parentNode.insertBefore(d,a);})();',
          },
        ]}
      />
      <Head>
        <title>Landkreditt Forsikring Min Side</title>
      </Head>
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      <SessionProvider session={session} refetchInterval={interval}>
        <QueryClientProvider client={queryClient}>
          {router && (router.pathname === '/validateEmail' || router.pathname === '/selectCompany') ? (
            <Component></Component>
          ) : (
            <AuthGuard pageComponent={Component} session={session} setInterval={setInterval}>
              <Hydrate state={pageProps.dehydratedState}>{getLayout(<Component {...pageProps} />)}</Hydrate>{' '}
            </AuthGuard>
          )}

          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </SessionProvider>
    </AppThemeProvider>
  );
}

CustomApp.getInitialProps = async (appContext: AppContext): Promise<AppInitialProps & Partial<CustomAppProps>> => {
  const appProps = await App.getInitialProps(appContext);

  /**
   * This is a workaround for the time being due to a known bug.
   * Bug was identified and respective developer notified.
   * --Gin
   */
  let preferredLanguage = I18N_DEFAULT_LANGUAGE;

  if (appContext.ctx.req) {
    preferredLanguage = getSSRCookie(appContext.ctx.req, 'i18next') || I18N_DEFAULT_LANGUAGE;

    await i18nNextInit({ useLanguageDetector: true, useBackend: true, preferredLanguage });
  }

  /**
   * -------------------End of workaround---------------------------------
   */
  const baseUrl = publicRuntimeConfig.hostUrl;
  let initialLanguageBundle;

  try {
    initialLanguageBundle = await fetch(`${baseUrl}/locales/${preferredLanguage}/translation.json`).then((r) =>
      r.json()
    );
  } catch {
    //ignore this error for now
  }

  const session = (await getSession({ req: appContext.ctx.req })) as never;

  return { ...appProps, preferredLanguage, initialLanguageBundle, session };
};

const GlobalStyle = createGlobalStyle`

  @font-face {
    font-family: 'Eina 03';
    src: url('/fonts/Eina03-Regular.otf') format("opentype");
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Eina 03';
    src: url('/fonts/Eina03-Bold.otf') format("opentype");
    font-weight: bold;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Eina 03';
    src: url('/fonts/Eina03-Light.otf') format("opentype");
    font-weight: 300;
    font-style: normal;
    font-display: block;
  }

  @font-face {
    font-family: 'Eina 03';
    src: url('/fonts/Eina03-SemiboldItalic.otf') format("opentype");
    font-weight: 600;
    font-style: italic;
    font-display: block;
  }

  @font-face {
    font-family: 'Eina 03 RegularItalic';
    src: url('/fonts/Eina03-RegularItalic.otf') format("opentype");
    font-weight: normal;
    font-style: italic;
    font-display: block;
  }

  @font-face {
    font-family: 'Eina 03';
    src: url('/fonts/Eina03-SemiBold.otf') format("opentype");
    font-weight: 600;
    font-style: normal;
    font-display: block;
  }

  @font-face {
    font-family: 'Eina 03';
    src: url('/fonts/Eina03-LightItalic.otf') format("opentype");
    font-weight: 300;
    font-style: italic;
    font-display: block;
  }

  @font-face {
    font-family: 'Eina 03';
    src: url('/fonts/Eina03-BoldItalic.otf') format("opentype");
    font-weight: bold;
    font-style: italic;
    font-display: block;
  }

  body {
    margin: 0;
    overflow: auto;
    font-family: 'Eina 03', 'Roboto', sans-serif !important;
  }
  .rc-select-dropdown{
    z-index: 999;
  }
  
  .rc-select-multiple:focus-within {
    border-color: #758B95!important;
    box-shadow: 0px 0px 0px 3px #ebeef0!important;
    border: 1px!important;
    border-radius: 6px!important;
    width: 100%!important;
    border-style: solid!important;
    border-width: 1px!important;
}

.rc-select-item.rc-select-item-option-active {
  box-shadow: unset!important;
  border: unset;
  outline: unset;
}

`;

export default CustomApp;
